import {
  adminActions,
  adminCompleteContract,
  adminGetCompleteContract,
  getPharmacyCompleteContract,
  getProfessionalCompleteContract,
  getTypeOfUser,
  pharmacistActions,
  PharmacistLegendVariant,
  pharmacyActions,
  pharmacyCompleteContract,
  PharmacyLegendVariant,
  professionalCompleteContract,
  TypeOfUser,
  WorkshiftStatus,
} from '@pharmaplan/common';
import { useAppSelector } from '../useAppSelector';
import { useAppDispatch } from '../useAppDispatch';
import useGetItems from '../useGetItems';
import { ChipType } from '../../helpers/Constants';
import strings from '../../localization';

const { pharmacist, pharmacy, technician, student, admin, superAdmin } = TypeOfUser;

interface IViewAllLongTermContracts {
  longTermContractId?: string;
  type: PharmacistLegendVariant | PharmacyLegendVariant;
  professionalId?: string;
}

const useViewAllLongTermContracts = ({
  longTermContractId,
  type,
  professionalId,
}: IViewAllLongTermContracts) => {
  const dispatch = useAppDispatch();

  const userType = useAppSelector(getTypeOfUser);
  const { getImage } = useGetItems();

  const professionalContract = useAppSelector(professionalCompleteContract);
  const pharmacyContract = useAppSelector(pharmacyCompleteContract);
  const adminContract = useAppSelector(adminCompleteContract);

  const professionalAPI = () => {
    dispatch(
      getProfessionalCompleteContract({
        longTermContractId: longTermContractId ?? '',
        type,
      }),
    );
  };

  const pharmacyAPI = () => {
    dispatch(
      getPharmacyCompleteContract({
        longTermContractId: longTermContractId ?? '',
        type,
        professionalId,
      }),
    );
  };

  const adminAPI = () => {
    dispatch(adminGetCompleteContract({
      longTermContractId: longTermContractId ?? '',
      type,
      professionalId,
    }));
  };

  const api = {
    [pharmacist]: professionalAPI,
    [student]: professionalAPI,
    [technician]: professionalAPI,
    [pharmacy]: pharmacyAPI,
    [admin]: adminAPI,
    [superAdmin]: adminAPI,
  };

  const contract = {
    [pharmacist]: professionalContract,
    [student]: professionalContract,
    [technician]: professionalContract,
    [pharmacy]: pharmacyContract,
    [admin]: adminContract,
    [superAdmin]: adminContract,
  };

  const {
    contractEndDate,
    contractStartDate,
    weekDayPaidHourlyRate,
    weekendPaidHourlyRate,
    allowanceNotes = '',
    name,
  } = contract[userType as keyof typeof contract] ?? {};

  const longTermContract = {
    weekDayPaidHourlyRate,
    weekendPaidHourlyRate,
    longTermShiftContractId: longTermContractId ?? '',
    startDate: contractStartDate,
    endDate: contractEndDate,
    allowanceNotes,
  };

  const pharmacistEvent = {
    longTermShiftContract: longTermContract,
    pharmacyName: name,
    startDate: contractStartDate,
    softwareName: professionalContract.softwareName || (pharmacyContract?.softwareName ?? ''),
    logoImage: getImage((professionalContract.logoId)),
  };

  const pharmacyEvent = {
    name,
    postalCode: pharmacyContract.postalCode,
    profilePicture: pharmacyContract.profilePicture,
    longTermShiftContract: longTermContract,
  };

  const adminEvent = {
    longTermShiftContract: longTermContract,
    pharmacyName: name,
    startDate: contractStartDate,
    softwareName: professionalContract.softwareName || (pharmacyContract?.softwareName ?? ''),
    logoImage: getImage((professionalContract.logoId)),
  };

  const userData = {
    [pharmacist]: pharmacistEvent,
    [student]: pharmacistEvent,
    [technician]: pharmacistEvent,
    [pharmacy]: type === PharmacistLegendVariant.workshift ? pharmacistEvent : pharmacyEvent,
    [admin]: adminEvent,
    [superAdmin]: adminEvent,
  };

  const successAction = {
    [pharmacist]: pharmacistActions.getProfessionalCompleteContract,
    [student]: pharmacistActions.getProfessionalCompleteContract,
    [technician]: pharmacistActions.getProfessionalCompleteContract,
    [pharmacy]: pharmacyActions.getPharmacyCompleteContract,
    [admin]: adminActions.adminGetCompleteContract,
    [superAdmin]: adminActions.adminGetCompleteContract,
  };

  const professionalStatus = {
    [WorkshiftStatus.Booked]: {
      type: ChipType.notAvailable,
      label: strings.notAvailable,
    },
    [WorkshiftStatus.NotAvailable]: {
      type: ChipType.notAvailable,
      label: strings.notAvailable,
    },
  };

  const pharmacyStatus = {
    [WorkshiftStatus.Booked]: {
      type: PharmacyLegendVariant.booked,
      label: strings.booked,
    },
    [WorkshiftStatus.NotAvailable]: {
      type: PharmacyLegendVariant.booked,
      label: strings.booked,
    },
  };

  const status = {
    [pharmacist]: professionalStatus,
    [student]: professionalStatus,
    [technician]: professionalStatus,
    [pharmacy]: pharmacyStatus,
    [admin]: pharmacyStatus,
    [superAdmin]: pharmacyStatus,
  };

  return {
    status: status[userType as keyof typeof status],
    getAPI: api[userType as keyof typeof api],
    contract: contract[userType as keyof typeof contract],
    userData: userData[userType as keyof typeof userData],
    successAction: successAction[userType as keyof typeof successAction],
    longTermContract,
  };
};

export default useViewAllLongTermContracts;
